import { equals, sort } from 'ramda';
import { sortPartialDate } from 'neuro-utils';
import { capitalize } from '../../../utility/string';
import { path } from 'Utility/ramdaReplacement';

/**
 * Find active VNS treatment
 * @param {IVNS[]} documents - All VNS documents
 * @returns {IVNS[]} - Active VNS documents
 */
export const findActive = <T extends IVNS>(documents: T[]): T[] =>
  documents.filter((d: IVNS) => d.hasEnded?.[0] !== true);

/**
 * Find ended VNS
 * @param {IVNS[]} documents - All VNS documents
 * @returns {IVNS[]} Ended VNS documents
 */
export const findEnded = <Type extends 'dbs' | 'vns' = 'dbs' | 'vns'>(
  documents: IDocumentWithTypeV2<Type>[],
): IDocumentWithTypeV2<Type>[] => documents.filter(doc => doc.hasEnded?.[0] === true);

/**
 * Get latest event from discontinuations
 * @param {IVNSDiscontinuationEvent[]} events - Discontinuations array
 * @returns {IVNSDiscontinuationEvent | undefined} - Latest discontinuation event or undefined
 */
export const getLatestDiscontinuation = (events: IVNS['discontinuations']): IVNSDiscontinuationEvent | undefined => {
  if (!events || events.length === 0) {
    return undefined;
  }
  const latestActive = sort(
    (n1: IVNSDiscontinuationEvent, n2: IVNSDiscontinuationEvent) => sortPartialDate(n2.date, n1.date),
    events,
  ).find(d => !d.endDate);
  return latestActive;
};

/**
 * Find latest VNS generator
 * @param {IVNS} document - The VNS where the generator is looked for
 * @returns {IVNSGenerator | undefined} - The latest VNS generator, undefined if no generator is found
 */
export const getLatestGenerator = (document: IVNS): IVNSGenerator | undefined =>
  path(
    [0],
    (document.generators ?? []).toSorted((a, b) => sortPartialDate(b.date, a.date)),
  ) ?? undefined;

/**
 * Find the generator that follows given generator chronologically
 * @param {IVNSGenerator} generator - The generator from which to start looking for the next generator
 * @param {IVNS} document - The VNS document where the next generator is looked for
 * @returns {IVNSGenerator | undefined} - The next generator if found, undefined if not
 */
export const findNextGenerator = (generator: IVNSGenerator, document: IVNS): IVNSGenerator | undefined => {
  const generatorsSorted = (document.generators ?? []).toSorted((a, b) => sortPartialDate(b.date, a.date));
  const currentIndex = generatorsSorted.findIndex(g => equals(generator, g));

  return currentIndex > 0 ? generatorsSorted[currentIndex - 1] : undefined;
};

/**
 * Handle discontinuations and continuations in VNS form and set treatment status accordingly
 * @param {IFormData['onChange']} onChange - onChange function for updating document values
 * @param {IVNSDiscontinuationEvent[] | undefined} discontinuations - Array of discontinuation events in the document
 * @param {IVNSGenerator[] | undefined} generators - Array of generators in the document
 * @param {string} status - Current status of the treatment
 * @param {boolean} hasEnded - True if treatment has been marked as ended, false if not
 */
export const discontinuationHandler = (
  onChange: IFormData['onChange'],
  discontinuations: IVNS['discontinuations'],
  generators: IVNS['generators'],
  status: IVNS['status'],
  hasEnded: boolean,
): void => {
  const newDiscontinuations = structuredClone(discontinuations ?? []);
  const removalDiscontinuations = newDiscontinuations.filter(
    discontinuation => discontinuation.type !== 'generatorSwitchedOff',
  );

  const newGenerators = structuredClone(generators ?? []);
  const latestDiscontinuation = getLatestDiscontinuation(newDiscontinuations);

  const updateDiscontinuationEndDates = (): void => {
    newGenerators.forEach((g: IVNSGenerator): void => {
      newDiscontinuations.forEach((d: IVNSDiscontinuationEvent, i: number): void => {
        if (d.type !== 'generatorSwitchedOff' && sortPartialDate(g.date, d.date) >= 0) {
          newDiscontinuations[i] = { ...d, endDate: g.date };
          return;
        }
      });
    });
  };

  const removeDiscontinuationEndDates = (): void => {
    removalDiscontinuations
      .filter(
        discontinuation =>
          !newGenerators
            .slice(0, newGenerators.length - 1)
            .some(generator => equals(discontinuation.endDate, generator.date)),
      )
      .forEach((r: IVNSDiscontinuationEvent) => {
        const targetIndex = newDiscontinuations.indexOf(r);
        const targetDiscontinuation = newDiscontinuations[targetIndex];
        if (targetIndex >= 0 && targetDiscontinuation) {
          newDiscontinuations[targetIndex] = { ...targetDiscontinuation, endDate: undefined };
        }
      });
  };

  if (hasEnded) {
    if (status !== 'ended') onChange?.({ status: 'ended' });
  }

  if (!Array.isArray(newDiscontinuations) || newDiscontinuations.length < 1) {
    if (!hasEnded && status !== 'inProgress') onChange?.({ status: 'inProgress' });
  }

  if (latestDiscontinuation) {
    if (latestDiscontinuation.type === 'generatorSwitchedOff') {
      if (!hasEnded && status !== 'suspended') onChange?.({ status: 'suspended' });
    } else {
      if (newGenerators.length < removalDiscontinuations.length + 1) {
        if (!hasEnded && status !== 'suspended') onChange?.({ status: 'suspended' });
      }
      if (newGenerators.length >= removalDiscontinuations.length + 1) {
        updateDiscontinuationEndDates();
        removeDiscontinuationEndDates();

        if (!equals(newDiscontinuations, discontinuations)) onChange?.({ discontinuations: newDiscontinuations });
      }
      if (removalDiscontinuations.find(discontinuation => !discontinuation.endDate)) {
        if (!hasEnded && status !== 'suspended') onChange?.({ status: 'suspended' });
      }
    }
  } else {
    if (newGenerators.length >= removalDiscontinuations.length + 1) {
      if (!hasEnded && status !== 'inProgress') onChange?.({ status: 'inProgress' });

      updateDiscontinuationEndDates();
      removeDiscontinuationEndDates();

      if (!equals(newDiscontinuations, discontinuations)) onChange?.({ discontinuations: newDiscontinuations });
    } else {
      if (!hasEnded && status !== 'suspended') onChange?.({ status: 'suspended' });

      removeDiscontinuationEndDates();

      if (!equals(newDiscontinuations, discontinuations)) onChange?.({ discontinuations: newDiscontinuations });
    }
  }
};

/**
 * Manage generator settings based on actions/changes done to generator(s)
 * @param {IFormData} formData - formData that has VNS documents and onChange function for updating the values
 * @param {'generator_deleted' | 'generator_changed' | 'generator_date_changed'} action - type of action done to generator(s)
 * @param {IVNSGenerator} generator - the generator related to the change
 */
export const generatorSettingHandler = (
  formData: IFormData<IDocumentWithTypeV2<'vns'>>,
  action: 'generator_deleted' | 'generator_changed' | 'generator_date_changed',
  generator?: IVNSGenerator,
): void => {
  const arraysAreValid = Array.isArray(formData.document.settings) && Array.isArray(formData.document.generators);
  switch (action) {
    case 'generator_deleted':
    case 'generator_changed': {
      if (arraysAreValid) {
        const newSettings = formData.document.settings?.filter(
          (setting: IVNSSetting) => setting.generator !== generator?.id,
        );
        formData.onChange?.({ settings: newSettings });
      }
      break;
    }
    default: {
      return;
    }
  }
};

/**
 * Determine if VNS settings row column has number field in it
 * @param {string} column - Column name
 * @param {string} row - Row name
 * @param {boolean | undefined} night - True if night row differs from default row
 * @returns {string} - True if column has number field, false if not
 */
export const columnHasNumberField = (column: string, row: string, night?: boolean): boolean => {
  if (column === 'initialization') {
    return false;
  }
  if (
    ['autoStimMode', 'magnetMode'].includes(column) &&
    ['offTime', 'efficiencyCycle', 'autoStimThreshold'].includes(row)
  ) {
    return false;
  }
  if (['autoStimMode', 'magnetMode'].includes(column) && row === 'rate' && night) {
    return false;
  }
  return true;
};

/**
 * Get object property keys for different types of events
 * @param {string} type - Event type
 * @returns {string} - List of object property keys for given event
 */
export const keysByType = (type: string): Array<string> => {
  switch (type) {
    case 'lead':
      return ['electrodeMountingLocation', 'leadType', 'leadSerialNo', 'leadUDIDICode', 'implantationSite'];
    case 'modeActivation':
      return ['activationsPerDay', 'additionalInformation'];
    case 'postoperativeComplication':
      return ['complications', 'additionalInformation'];
    case 'adverseEffect':
      return ['effects', 'additionalInformation'];
    case 'technicalIssue':
      return ['issue', 'additionalInformation'];
    case 'discontinuation':
      return ['reason', 'type', 'lengthOfRemainingLead', 'additionalInformation', 'endDate'];
    case 'engelClassification':
      return ['timeFromOperation', 'classification'];
    default:
      return [];
  }
};

/**
 * Convert object property keys to header ids used in localization
 * @param {string} key - Object property key
 * @returns {string} - Header id for given key
 */
export const keyToHeader = (key: string): string => {
  switch (key) {
    case 'effects':
      return 'adverseEffects';
    case 'issue':
      return 'technicalIssue';
    case 'reason':
    case 'type':
    case 'endDate':
      return `discontinuation${capitalize(key)}`;
    default:
      return key;
  }
};
